import { useEffect, useState } from 'react';
import {
  resellerEmailSettings,
  type ResellerEmailView,
  type ResellerEmailPutBody,
} from '@/api/resellerEmailSettings';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuth } from '@/lib/auth-store';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Navigate } from 'react-router-dom';
import { cn } from '@/lib/cn';

type Mode = 'platform' | 'own' | 'null';
type OwnProvider = 'sendgrid' | 'gmail_smtp' | 'smtp';

export function ResellerEmailSettings() {
  const me = useCurrentUser();
  const tenant = useAuth((s) => s.tenant);
  // Reseller owner: use their own reseller_id from the session tenant scope.
  // Super admin lands here? Bounce them — they manage this from /settings/email.
  if (me.role !== 'reseller_owner' || !tenant?.resellerId) {
    return <Navigate to="/dashboard" replace />;
  }

  return <ResellerEmailSettingsInner resellerId={tenant.resellerId} />;
}

function ResellerEmailSettingsInner({ resellerId }: { resellerId: string }) {
  const [current, setCurrent] = useState<ResellerEmailView | null>(null);
  const [mode, setMode] = useState<Mode>('platform');
  const [ownProvider, setOwnProvider] = useState<OwnProvider>('sendgrid');
  const [sgApiKey, setSgApiKey] = useState('');
  const [sgFrom, setSgFrom] = useState('');
  const [gmEmail, setGmEmail] = useState('');
  const [gmPass, setGmPass] = useState('');
  // generic SMTP fields
  const [smHost, setSmHost] = useState('');
  const [smPort, setSmPort] = useState('587');
  const [smSecure, setSmSecure] = useState(false);
  const [smUser, setSmUser] = useState('');
  const [smPass, setSmPass] = useState('');
  const [smFrom, setSmFrom] = useState('');
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    resellerEmailSettings.get(resellerId).then((v) => {
      if (ignore) return;
      setCurrent(v);
      setMode(v.mode);
      if (v.own.provider) setOwnProvider(v.own.provider);
      if (v.own.provider === 'sendgrid' && v.own.credentials_redacted) {
        setSgFrom(v.own.credentials_redacted.from ?? '');
      } else if (v.own.provider === 'gmail_smtp' && v.own.credentials_redacted) {
        setGmEmail(v.own.credentials_redacted.email ?? '');
      } else if (v.own.provider === 'smtp' && v.own.credentials_redacted) {
        setSmHost(v.own.credentials_redacted.host ?? '');
        setSmPort(v.own.credentials_redacted.port ?? '587');
        setSmSecure(v.own.credentials_redacted.secure === 'true');
        setSmUser(v.own.credentials_redacted.user ?? '');
        setSmFrom(v.own.credentials_redacted.from ?? '');
      }
    }).catch(() => { /* fresh state */ });
    return () => { ignore = true; };
  }, [resellerId]);

  async function save() {
    setStatus(null);
    let body: ResellerEmailPutBody;
    if (mode === 'platform') body = { mode: 'platform' };
    else if (mode === 'null') body = { mode: 'null' };
    else {
      if (ownProvider === 'sendgrid') {
        body = { mode: 'own', own: { provider: 'sendgrid', credentials: { apiKey: sgApiKey, from: sgFrom } } };
      } else if (ownProvider === 'gmail_smtp') {
        body = { mode: 'own', own: { provider: 'gmail_smtp', credentials: { email: gmEmail, appPassword: gmPass } } };
      } else {
        body = {
          mode: 'own',
          own: {
            provider: 'smtp',
            credentials: {
              host: smHost.trim(),
              port: Number(smPort),
              secure: smSecure,
              user: smUser.trim(),
              password: smPass,
              from: smFrom.trim(),
            },
          },
        };
      }
    }
    try {
      await resellerEmailSettings.put(resellerId, body);
      setStatus('Saved.');
      const next = await resellerEmailSettings.get(resellerId);
      setCurrent(next);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Save failed.');
    }
  }

  const platformAvailable = current?.platform_effective_available === true;
  const platformDisabledReason = !current
    ? null
    : !current.platform_allow_reseller_use
      ? 'The platform owner has not enabled shared sender for any reseller.'
      : !current.per_reseller_platform_allowed
        ? 'The platform owner has revoked shared-sender access for your reseller account.'
        : null;

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Email"
        description="Configure how your customers and users receive invitations, password resets, and notifications from Ivrello."
      />
      <div className="max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Sender</CardTitle>
            <CardDescription>
              Choose whether to use the platform&rsquo;s shared sender, bring your own provider,
              or disable email entirely.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div role="radiogroup" aria-label="Sender mode" className="grid gap-2">
              <ModeOption
                value="platform"
                mode={mode}
                setMode={setMode}
                title="Platform sender"
                disabled={!platformAvailable}
                description={
                  platformAvailable
                    ? 'Route all outbound email through the platform owner\u2019s sender. Simplest option \u2014 no credentials to manage.'
                    : platformDisabledReason ?? 'Not available.'
                }
              />
              <ModeOption
                value="own"
                mode={mode}
                setMode={setMode}
                title="Bring your own"
                description="Use your own SendGrid or Gmail SMTP credentials. Full control over sender identity and deliverability."
              />
              <ModeOption
                value="null"
                mode={mode}
                setMode={setMode}
                title="Disabled"
                description="Nothing sends. Useful while you evaluate providers."
              />
            </div>

            {mode === 'own' && (
              <div className="pt-2 border-t border-border space-y-4">
                <div className="pt-4">
                  <Label>Provider</Label>
                  <div className="mt-1 grid gap-2 sm:grid-cols-3">
                    {(['sendgrid', 'gmail_smtp', 'smtp'] as const).map((p) => (
                      <label
                        key={p}
                        className={cn(
                          'cursor-pointer rounded-md border px-4 py-3 text-sm flex items-center gap-2 transition-colors',
                          ownProvider === p
                            ? 'border-primary bg-primary/5 text-foreground'
                            : 'border-border hover:bg-secondary/40',
                        )}
                      >
                        <input
                          type="radio"
                          name="ownProvider"
                          checked={ownProvider === p}
                          onChange={() => setOwnProvider(p)}
                          className="accent-primary"
                        />
                        <span>
                          {p === 'sendgrid' ? 'SendGrid' : p === 'gmail_smtp' ? 'Gmail SMTP' : 'Generic SMTP'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                {ownProvider === 'sendgrid' && (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="sgApiKey">API key</Label>
                      <Input
                        id="sgApiKey" type="password" value={sgApiKey}
                        onChange={(e) => setSgApiKey(e.target.value)}
                        placeholder={current?.own.credentials_redacted?.apiKey ?? 'SG....'}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="sgFrom">From address</Label>
                      <Input id="sgFrom" type="email" value={sgFrom} onChange={(e) => setSgFrom(e.target.value)} />
                    </div>
                  </div>
                )}
                {ownProvider === 'gmail_smtp' && (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="gmEmail">Gmail address</Label>
                      <Input id="gmEmail" type="email" value={gmEmail} onChange={(e) => setGmEmail(e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="gmPass">App password</Label>
                      <Input
                        id="gmPass" type="password" value={gmPass}
                        onChange={(e) => setGmPass(e.target.value)}
                        placeholder={current?.own.credentials_redacted?.appPassword ?? '16-char app password'}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Use a Google app password, not your main Google account password.
                    </p>
                  </div>
                )}
                {ownProvider === 'smtp' && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-[1fr_6rem] gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="smHost">SMTP host</Label>
                        <Input
                          id="smHost" value={smHost}
                          onChange={(e) => setSmHost(e.target.value)}
                          placeholder={current?.own.credentials_redacted?.host ?? 'smtp.mailgun.org'}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="smPort">Port</Label>
                        <Input
                          id="smPort" type="number" min="1" max="65535"
                          value={smPort} onChange={(e) => setSmPort(e.target.value)}
                          className="font-mono"
                        />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={smSecure}
                        onChange={(e) => setSmSecure(e.target.checked)}
                        className="accent-primary"
                      />
                      <span>Use implicit TLS (port 465) — uncheck for STARTTLS (587)</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="smUser">Username</Label>
                        <Input
                          id="smUser" value={smUser}
                          onChange={(e) => setSmUser(e.target.value)}
                          placeholder={current?.own.credentials_redacted?.user ?? 'postmaster@…'}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="smPassField">Password</Label>
                        <Input
                          id="smPassField" type="password" value={smPass}
                          onChange={(e) => setSmPass(e.target.value)}
                          placeholder={current?.own.credentials_redacted?.password ?? ''}
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="smFrom">From address</Label>
                      <Input
                        id="smFrom" type="email" value={smFrom}
                        onChange={(e) => setSmFrom(e.target.value)}
                        placeholder={current?.own.credentials_redacted?.from ?? 'support@yourbrand.com'}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Works with Mailgun, Postmark, Mailjet, AWS SES SMTP endpoint, Postfix, etc. Anywhere you have an SMTP relay.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="pt-2">
              <Button onClick={save}>Save</Button>
            </div>
            {status && <p className="text-sm" role="alert">{status}</p>}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function ModeOption({
  value,
  mode,
  setMode,
  title,
  description,
  disabled,
}: {
  value: Mode;
  mode: Mode;
  setMode: (m: Mode) => void;
  title: string;
  description: string;
  disabled?: boolean;
}) {
  const selected = mode === value;
  return (
    <label
      className={cn(
        'rounded-md border p-4 flex items-start gap-3 transition-colors',
        disabled
          ? 'opacity-50 cursor-not-allowed border-border'
          : selected
            ? 'cursor-pointer border-primary bg-primary/5'
            : 'cursor-pointer border-border hover:bg-secondary/40',
      )}
    >
      <input
        type="radio"
        name="mode"
        checked={selected}
        disabled={disabled}
        onChange={() => !disabled && setMode(value)}
        className="mt-0.5 accent-primary"
      />
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
    </label>
  );
}
